import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Divider,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import authService from '../../services/authService';
import useAccess from '../../hooks/useAccess';
import { AccessLevel } from '../../types';

/** Must match MIN_PASSWORD_LENGTH in school-service, which rejects anything shorter. */
const MIN_PASSWORD_LENGTH = 8;

const ACCESS_DESCRIPTIONS: Record<AccessLevel, string> = {
  [AccessLevel.FULL]: 'Full access — can view and change everything',
  [AccessLevel.READ_ONLY]: 'Read-only — can view everything, but not make changes',
  [AccessLevel.CHANNEL]: 'Channel access — limited to your own channel',
};

const ProfilePage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const access = useAccess();

  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPasswords, setShowPasswords] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setSubmitError(null);
    setSuccess(false);
  };

  const validate = () => {
    const next: { [key: string]: string } = {};

    if (!form.currentPassword) {
      next.currentPassword = 'Enter your current password';
    }
    if (!form.newPassword) {
      next.newPassword = 'Enter a new password';
    } else if (form.newPassword.length < MIN_PASSWORD_LENGTH) {
      next.newPassword = `Must be at least ${MIN_PASSWORD_LENGTH} characters`;
    } else if (form.newPassword === form.currentPassword) {
      next.newPassword = 'Must differ from your current password';
    }
    if (form.confirmPassword !== form.newPassword) {
      next.confirmPassword = 'Passwords do not match';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setSubmitError(null);
    try {
      await authService.changePassword(form.currentPassword, form.newPassword);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const visibilityToggle = {
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={() => setShowPasswords((shown) => !shown)} edge="end" size="small">
          {showPasswords ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        User Profile
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
        <Card sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Name
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {user?.name || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {user?.email || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Access
            </Typography>
            <Chip size="small" label={ACCESS_DESCRIPTIONS[access.level]} sx={{ mb: 1 }} />
            {access.channel && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Channel: <strong>{access.channel}</strong>
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card sx={{ flex: '2 1 420px', minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Change Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You will stay signed in on this device after changing it.
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Password changed successfully.
              </Alert>
            )}

            {submitError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {submitError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Current Password"
                type={showPasswords ? 'text' : 'password'}
                value={form.currentPassword}
                onChange={(e) => update('currentPassword', e.target.value)}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword}
                autoComplete="current-password"
                InputProps={visibilityToggle}
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="New Password"
                type={showPasswords ? 'text' : 'password'}
                value={form.newPassword}
                onChange={(e) => update('newPassword', e.target.value)}
                error={!!errors.newPassword}
                helperText={errors.newPassword || `At least ${MIN_PASSWORD_LENGTH} characters`}
                autoComplete="new-password"
                InputProps={visibilityToggle}
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type={showPasswords ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={(e) => update('confirmPassword', e.target.value)}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                autoComplete="new-password"
                InputProps={visibilityToggle}
                sx={{ mb: 3 }}
              />
              <Button type="submit" variant="contained" disabled={saving} sx={{ fontWeight: 600 }}>
                {saving ? 'Changing...' : 'Change Password'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ProfilePage;
